import { config } from "shared/data";

import {
  AI_WIDGET_API_TIMEOUT_MS,
  AI_WIDGET_FETCH_API_ENDPOINT,
  AI_WIDGET_FETCH_LOG_PREFIX,
} from "./constants";
import type { AiWidgetApiResponse, AiWidgetEntityIds } from "./types";

type ApiRequestParameters = {
  excludeArticleIds?: string[];
  reqRegion: string;
  userId?: string;
  visitorId: string;
};

const getRecommendParameters = () => {
  if (config.general.env === "production" || config.general.env === "preprod") {
    return;
  }

  const now = new Date();
  const endDate = new Date(now);
  endDate.setDate(now.getDate() - 8);
  const startDate = new Date(endDate);
  startDate.setDate(endDate.getDate() - 6);

  return {
    recall: {
      endDate: endDate.toISOString().split("T")[0],
      startDate: startDate.toISOString().split("T")[0],
    },
  };
};

export const fetchAiWidgetRecommendations = async (
  parameters: ApiRequestParameters,
): Promise<AiWidgetApiResponse> => {
  const recommendParameters = getRecommendParameters();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AI_WIDGET_API_TIMEOUT_MS);

  try {
    const response = await fetch(AI_WIDGET_FETCH_API_ENDPOINT, {
      body: JSON.stringify({
        ...(recommendParameters && { recommendParams: recommendParameters }),
        excludeArticleIds: parameters.excludeArticleIds,
        reqRegion: parameters.reqRegion,
        userId: parameters.userId,
        visitorId: parameters.visitorId,
      }),
      headers: { apikey: config.abWidgetApi.apiKey, "Content-Type": "application/json" },
      method: "POST",
      signal: controller.signal,
    });

    // TODO: Add proper error handling — for now throw on non-200
    if (!response.ok) {
      console.error(`${AI_WIDGET_FETCH_LOG_PREFIX} API error: ${response.status}`);
      throw new Error(`AI widget API error: ${response.status}`);
    }

    return response.json() as Promise<AiWidgetApiResponse>;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Map API response widgets to named entityIds by widget type key.
 * Keys: feature_package_1 → story package one (slot 1, historically "series"),
 * feature_package_2 → story package two (slot 2, historically "open question"),
 * geo → CFT, spotlight → Spotlight.
 * CMS queue names, API widget type strings, and GA4 widget_name values
 * intentionally keep their historical values — they are external contracts.
 */
export const massageApiResponse = (response: AiWidgetApiResponse): AiWidgetEntityIds => {
  const widgets = response.data.recommendationArea.widgets;

  const widgetMap = new Map<string, string[]>();
  const widgetTopicEntityIdMap = new Map<string, string>();
  const widgetTopicNameMap = new Map<string, string>();

  for (const widget of widgets) {
    const entityIds = (widget.items ?? [])
      .toSorted((a, b) => a.slotId - b.slotId)
      .map(item => item.article.articleId);
    widgetMap.set(widget.type, entityIds);
    if (widget.topic?.id) {
      widgetTopicEntityIdMap.set(widget.type, widget.topic.id);
    }
    if (widget.topic?.name) {
      widgetTopicNameMap.set(widget.type, widget.topic.name);
    }
  }

  const result: AiWidgetEntityIds = {
    cft: widgetMap.get("geo") ?? [],
    cftTopicEntityId: widgetTopicEntityIdMap.get("geo"),
    cftTopicName: widgetTopicNameMap.get("geo"),
    recommendationId: response.data.requestId,
    spotlight: widgetMap.get("spotlight") ?? [],
    storyPackageOne: (widgetMap.get("feature_package_1") ?? []).slice(0, 3),
    storyPackageOneRaw: widgetMap.get("feature_package_1") ?? [],
    storyPackageOneTopicEntityId: widgetTopicEntityIdMap.get("feature_package_1"),
    storyPackageOneTopicName: widgetTopicNameMap.get("feature_package_1"),
    storyPackageTwo: widgetMap.get("feature_package_2") ?? [],
    storyPackageTwoTopicEntityId: widgetTopicEntityIdMap.get("feature_package_2"),
    storyPackageTwoTopicName: widgetTopicNameMap.get("feature_package_2"),
  };

  return result;
};

export const getColdStart = (response: AiWidgetApiResponse): boolean =>
  response.data.reader.coldStart;
